import { useEffect, useRef, useState } from 'react';
import { ReactP5Wrapper } from '@p5-wrapper/react';
import {
	calculateDrawSize,
	displaySketch,
	editSketch,
	setParameters,
	getParameters,
	setCanvasDimensions,
	canUndo,
	canRedo,
	performUndo,
	performRedo,
} from '../libs/BitmapEditor/bitmapMaker3';
import styled from 'styled-components';
import { useForceUpdate } from '../hooks/forceUpdate';
import Box from '@mui/material/Box';
import { simulateMouseClick } from '../helpers/click';
import Tools from './Tools';
import TopBar from './TopBar';
import FloatingSave from './FloatingSave';
import { getCreateWith } from '../helpers/createWith';
import { saveOnlineAndClose } from '../helpers/saveOnline';
import { CircularProgress } from '@mui/material';
import CanvasSizeModal from './CanvasSizeModal';
import BackgroundImageAdjustmentModal from './BackgroundImageAdjustmentModal';
import BackgroundConfirmCard from './BackgroundConfirmCard';
import useAutosave from '../hooks/useAutosave';
import { useColorHistoryContext } from '../contexts/ColorHistoryContext';

declare global {
	interface Window {
		editSketch: any;
		displaySketch: any;
	}
}

function Canvas() {
	const handleForceupdateMethod = useForceUpdate();
	const p5 = () => window.editSketch; // helper to get the p5 instance of editSketch
	const createWith = getCreateWith();
	const [isLoading, setIsLoading] = useState(false);
	const [canvasSize, setCanvasSize] = useState(310); // Default fallback size
	const appliedSizeRef = useRef(canvasSize);
	appliedSizeRef.current = canvasSize;
	const [canvasDimensionsSet, setCanvasDimensionsSet] = useState(false);
	// Setup wizard in the GUI route order. With an imported image: adjust it
	// (03), confirm it (04), then canvas size (01) and pencil size (02).
	// Without one: canvas size (01) and pencil size (02). Editing an existing
	// bitmap skips the wizard.
	const [setupStage, setSetupStage] = useState<'loading' | 'adjust' | 'confirm' | 'size' | 'done'>('loading');
	// Whether the user kept the imported image at the confirm step
	const keepBackgroundRef = useRef(true);
	// Canvas size chosen before an image turned up (see handleCanvasDimensionsConfirm)
	const pendingSizeRef = useRef<{ width: number; height: number } | null>(null);
	const [backgroundImageData, setBackgroundImageData] = useState<{
		image: string;
		grayscale: number;
		opacity: number;
	} | null>(null);
	const [undoAvailable, setUndoAvailable] = useState(false);
	const [isNewCreation, setIsNewCreation] = useState(true);
	// Import steps 01-02 happen in the app; 03-04 here, before the canvas steps
	const IMPORT_TOTAL_STEPS = 4;
	const CANVAS_TOTAL_STEPS = 2;
	const [redoAvailable, setRedoAvailable] = useState(false);

	// Color history context
	const { addColorToHistory } = useColorHistoryContext();

	// Poll undo/redo availability so the top toolbar reflects drawing actions
	useEffect(() => {
		const interval = setInterval(() => {
			setUndoAvailable(canUndo());
			setRedoAvailable(canRedo());
		}, 300);
		return () => clearInterval(interval);
	}, []);

	// Auto-save on the app's interval once the editor is ready (GUI route 16)
	useAutosave(canvasDimensionsSet);

	const handleUndo = () => {
		if (performUndo()) {
			setUndoAvailable(canUndo());
			setRedoAvailable(canRedo());
		}
	};

	const handleRedo = () => {
		if (performRedo()) {
			setUndoAvailable(canUndo());
			setRedoAvailable(canRedo());
		}
	};

	// Expose a global function so the host app can request "save + close"
	// without the user having tapped save in webdraw first.
	useEffect(() => {
		(window as any).cwpRequestSaveAndClose = async () => {
			// Still in the setup steps: nothing has been drawn, so there is nothing to
			// save. Just leave. Store builds of the app don't offer `dismiss`.
			if (!window.editSketch?.sketchReady) {
				getCreateWith()?.dismiss?.();
				return;
			}
			try {
				await saveOnlineAndClose();
			} catch (error) {
				console.error('cwpRequestSaveAndClose error:', error);
			}
		};
		return () => {
			delete (window as any).cwpRequestSaveAndClose;
		};
	}, []);

	// Decide where the setup wizard starts
	useEffect(() => {
		const startSetup = async () => {
			try {
				const previousBitmap = await createWith?.get?.();
				if (previousBitmap?.image?.image) {
					// Editing: the bitmap's own size is used and the wizard is skipped.
					// The sketch reads the artwork once when it starts, so it has to be
					// in the parameters before the canvas mounts. Set any later and the
					// canvas can open blank, and the next save would overwrite the artwork.
					const currentParams = getParameters();
					currentParams.snapshot = previousBitmap.image.image;
					setParameters(currentParams);
					setIsNewCreation(false);
					finishSetup(100, 100);
					return;
				}
			} catch (error) {
				console.error('Error checking for existing bitmap:', error);
			}
			try {
				const backgroundImage = await createWith?.get_background_image?.();
				if (backgroundImage?.image?.image) {
					setBackgroundImageData({
						image: backgroundImage.image.image,
						grayscale: backgroundImage.image.grayscale ?? 0,
						opacity: backgroundImage.image.opacity ?? 1,
					});
					setSetupStage('adjust');
					return;
				}
			} catch (error) {
				console.error('Error checking for background image:', error);
			}
			setSetupStage('size');
		};
		startSetup();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Calculate canvas size so the full canvas is always visible at a glance
	// between the fixed top bar and the fixed drawing tool bar.
	const calculateCanvasSize = () => {
		const screenWidth = window.innerWidth;
		const screenHeight = window.innerHeight;

		const reservedAbove = 104; // fixed top bar (zoom, full screen, undo/redo) + margins
		// Fixed drawing tool bar, so canvas and tools fit on one screen. Tablets
		// have room for the display timer as well; on phones it sits below the
		// fold so the canvas stays as large as possible.
		const isTablet = Math.min(screenWidth, screenHeight) >= 600;
		const reservedBelow = isTablet ? 156 : 96;
		const availableHeight = screenHeight - reservedAbove - reservedBelow;

		const widthBased = screenWidth * 0.95;
		const heightBased = availableHeight;

		const optimalSize = Math.min(widthBased, heightBased);

		// Min 280 (small phones), max 900 (large tablets)
		return Math.floor(Math.max(280, Math.min(900, optimalSize)));
	};

	// Store builds of the app load the editor before the user has imported an
	// image, so the check at startup finds nothing. Look again here so their
	// image still gets the adjust and confirm steps, just after the canvas size.
	const handleCanvasDimensionsConfirm = async (width: number, height: number) => {
		if (!backgroundImageData) {
			try {
				const backgroundImage = await createWith?.get_background_image?.();
				if (backgroundImage?.image?.image) {
					pendingSizeRef.current = { width, height };
					setBackgroundImageData({
						image: backgroundImage.image.image,
						grayscale: backgroundImage.image.grayscale ?? 0,
						opacity: backgroundImage.image.opacity ?? 1,
					});
					setSetupStage('adjust');
					return;
				}
			} catch (error) {
				console.error('Error checking for background image:', error);
			}
		}
		finishSetup(width, height);
	};

	// Last setup step before drawing: apply size and the kept background image
	const finishSetup = (width: number, height: number) => {
		const currentParams = getParameters();
		currentParams.width = width;
		currentParams.height = height;
		currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
		if (backgroundImageData && keepBackgroundRef.current) {
			currentParams.background_image = backgroundImageData.image;
			currentParams.background_grayscale = backgroundImageData.grayscale; // 0-100
			currentParams.background_opacity = backgroundImageData.opacity; // 0-1
		}
		setParameters(currentParams);

		const newSize = calculateCanvasSize();
		setCanvasSize(newSize);
		setCanvasDimensions(newSize, newSize);

		setSetupStage('done');
		setCanvasDimensionsSet(true);
	};

	// Step 03: Apply keeps the new values, Skip keeps the original ones
	const handleBackgroundAdjustmentConfirm = (grayscale: number, opacity: number) => {
		setBackgroundImageData(data => (data ? { ...data, grayscale, opacity } : data));
		setSetupStage('confirm');
	};

	const handleBackgroundAdjustmentSkip = () => {
		setSetupStage('confirm');
	};

	// Step 04: Proceed keeps the image, Skip draws without it
	const handleBackgroundConfirm = (keep: boolean) => {
		keepBackgroundRef.current = keep;
		if (pendingSizeRef.current) {
			finishSetup(pendingSizeRef.current.width, pendingSizeRef.current.height);
		} else {
			setSetupStage('size');
		}
	};

	// Set canvas size once on mount (only if dimensions are already set)
	useEffect(() => {
		if (canvasDimensionsSet) {
			const newSize = calculateCanvasSize();
			setCanvasSize(newSize);
			setCanvasDimensions(newSize, newSize);
		}
	}, [canvasDimensionsSet]);

	// Match the canvas to the current window. The view returns to the full image.
	const refitCanvas = () => {
		// An on-screen keyboard resizes the window on Android: leave the canvas alone
		const focused = document.activeElement?.tagName;
		if (focused === 'INPUT' || focused === 'TEXTAREA') return;
		const newSize = calculateCanvasSize();
		if (newSize === appliedSizeRef.current || !p5()) return;
		setCanvasSize(newSize);
		setCanvasDimensions(newSize, newSize);
		p5().resizeStage(newSize, newSize);
		setFullScreenCanvas();
	};

	// Refit when the window changes size: rotating a tablet or resizing it in split view
	useEffect(() => {
		if (!canvasDimensionsSet) return;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const handleResize = () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(refitCanvas, 150);
		};
		window.addEventListener('resize', handleResize);
		return () => {
			if (timer) clearTimeout(timer);
			window.removeEventListener('resize', handleResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasDimensionsSet]);

	const initialLoad = async () => {
		if (createWith && (createWith.get || createWith.get_background_image)) {
			try {
				setIsLoading(true);
				// Request the previous bitmap from the mobile app
				const previousBitmap = await createWith.get();
				const backgroundImage = await createWith.get_background_image();

				if (previousBitmap?.image?.image) {
					// Set the previous bitmap as background image (scale kept, so cropped)
					const currentParams = getParameters();
					currentParams.snapshot = previousBitmap.image.image;
					currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
					setParameters(currentParams);
				} else if (backgroundImage?.image?.image && keepBackgroundRef.current) {
					// Set the background image (scaled down to canvas size)
					const currentParams = getParameters();
					currentParams.background_image = backgroundImage.image.image;
					currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
					setParameters(currentParams);
				} else {
					// No previous bitmap or background image, just set the callback
					const currentParams = getParameters();
					currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
					setParameters(currentParams);
				}
			} catch (error) {
				console.error('Error loading previous bitmap:', error);
			} finally {
				setIsLoading(false);
			}
		} else {
			// Set the callback even if no createWith methods are available
			const currentParams = getParameters();
			currentParams.onColorUsed = addColorToHistory;
			setParameters(currentParams);
		}
		// add async promise to give canvas time to render properly
		await new Promise(resolve => setTimeout(resolve, 300));
		if (p5()) {
			// The WebView can still have been laying out when the size was first measured
			refitCanvas();
			setFullScreenCanvas();
			// simulate touch for mobile webView
			simulateMouseClick(document);
		}
	};

	useEffect(() => {
		if (canvasDimensionsSet) {
			initialLoad();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasDimensionsSet]);

	const updateZoom = (zoom: number) => {
		p5()?.updateZoom(zoom);
		handleForceupdateMethod();
	};

	const setFullScreenCanvas = () => {
		if (p5()) {
			const currentParams = getParameters();
			const drawSize = calculateDrawSize(currentParams.width, currentParams.height); // Use dynamic dimensions
			p5().setDrawSize(drawSize ?? 1);

			// Update the view center coordinates to the new center
			p5().viewCenterX = p5().canvasCentreX;
			p5().viewCenterY = p5().canvasCentreY;
			// Recalculate the screen adjustment for the new view center
			p5().updateScreenAdjust(); // You can call this function directly
			// Force re-render after the update
			setTimeout(() => {
				handleForceupdateMethod();
			}, 300);
		}
	};

	// Show loading spinner
	if (isLoading) {
		return <CircularProgress color="primary" />;
	}

	if (setupStage === 'loading') {
		return <CircularProgress color="primary" />;
	}

	if (setupStage === 'adjust' && backgroundImageData) {
		return (
			<BackgroundImageAdjustmentModal
				open
				backgroundImage={backgroundImageData.image}
				initialGrayscale={backgroundImageData.grayscale} // Already in 0-100 range from mobile app
				initialOpacity={backgroundImageData.opacity * 100} // Convert from 0-1 to 0-100 range
				onConfirm={handleBackgroundAdjustmentConfirm}
				onSkip={handleBackgroundAdjustmentSkip}
				step={3}
				totalSteps={IMPORT_TOTAL_STEPS}
			/>
		);
	}

	if (setupStage === 'confirm' && backgroundImageData) {
		return (
			<BackgroundConfirmCard
				open
				image={backgroundImageData.image}
				grayscale={backgroundImageData.grayscale}
				opacity={backgroundImageData.opacity}
				onProceed={() => handleBackgroundConfirm(true)}
				onSkip={() => handleBackgroundConfirm(false)}
				step={4}
				totalSteps={IMPORT_TOTAL_STEPS}
			/>
		);
	}

	if (setupStage === 'size') {
		return <CanvasSizeModal open onConfirm={handleCanvasDimensionsConfirm} step={1} totalSteps={CANVAS_TOTAL_STEPS} />;
	}

	// Show main canvas interface only after dimensions are set
	if (!canvasDimensionsSet) {
		return <CircularProgress color="primary" />;
	}

	return (
		<Screen canvasSize={canvasSize}>
			{(() => {
				const currentDrawSize = p5()?.getDrawSize?.();
				const fullDrawSize = calculateDrawSize(getParameters().width, getParameters().height);
				const isFullCanvas = currentDrawSize !== undefined && currentDrawSize === fullDrawSize;
				return (
					<TopBar
						zoomPercentage={p5()?.getZoomPercentage() || 100}
						onZoomIn={() => updateZoom(0.5)}
						onZoomOut={() => updateZoom(-0.5)}
						onFullScreen={setFullScreenCanvas}
						isFullScreen={isFullCanvas}
						onUndo={handleUndo}
						onRedo={handleRedo}
						canUndo={undoAvailable}
						canRedo={redoAvailable}
					/>
				);
			})()}

			<Box display="none">
				<ReactP5Wrapper sketch={displaySketch} />
			</Box>
			<EditCanvas canvasSize={canvasSize}>
				<ReactP5Wrapper sketch={editSketch} />
				<FloatingSave />
			</EditCanvas>

			<Tools initialPencilStep={isNewCreation ? { step: CANVAS_TOTAL_STEPS, totalSteps: CANVAS_TOTAL_STEPS } : null} />
		</Screen>
	);
}

export default Canvas;

const Screen = styled.div.withConfig({
	shouldForwardProp: prop => prop !== 'canvasSize',
})<{ canvasSize: number }>`
	margin: 0 auto;
	padding-top: 4px;
	width: ${props => props.canvasSize}px;
	max-width: 95vw;
`;

const EditCanvas = styled.div.withConfig({
	shouldForwardProp: prop => prop !== 'canvasSize',
})<{ canvasSize: number }>`
	position: relative;
	margin: 12px 0 0;
	width: ${props => props.canvasSize}px;
	height: ${props => props.canvasSize}px;
	max-width: ${props => props.canvasSize}px;
	max-height: ${props => props.canvasSize}px;
	border-radius: 15px;
	overflow: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: none;
	touch-action: none;
`;
